import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import PackagesApi from 'legacyfrontend/api/paquetes';
import { HotelFilterForm } from 'legacyfrontend/components/filters';
import { dataDefaultValues, ROUTE_KEYS, DATE_KEYS } from 'legacyfrontend/constants';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { formatWithMomentFormat } from 'legacyfrontend/utils/dateUtils';
import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import ErrorSnackbar from '../../ErrorSnackbar';

const { BUSCAR_X_HOTELES } = ROUTE_KEYS;

const parseDateFromURL = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const initialHotelSetter = (params) => {
  if (!params) {
    return {
      destination: null,
      checkInDate: null,
      checkOutDate: null,
      rooms: 1,
      adultsPerRoom: [2],
      childrenPerRoom: [0],
      childrenAges: [[]],
      residenceCountry: '',
    };
  }

  const result = {
    destination:
      params?.get('destinationId') && params?.get('destinationName')
        ? {
          id: params.get('destinationId'),
          cityCode: params.get('destinationId'),
          cityName: params.get('destinationName').trim(),
        }
        : null,
    checkInDate: params?.get('checkInDate')
      ? parseDateFromURL(params.get('checkInDate'))
      : null,
    checkOutDate: params?.get('checkOutDate')
      ? parseDateFromURL(params.get('checkOutDate'))
      : null,
    rooms: params?.get('rooms') ? Number(params.get('rooms')) : 1,
    adultsPerRoom: params?.get('adultsPerRoom')
      ? JSON.parse(params.get('adultsPerRoom'))
      : [2],
    childrenPerRoom: params?.get('childrenPerRoom')
      ? JSON.parse(params.get('childrenPerRoom'))
      : [0],
    childrenAges: params?.get('childrenAges')
      ? JSON.parse(params.get('childrenAges'))
      : [[]],
    residenceCountry: params?.get('residenceCountry') || '',
  };

  return result;
};

const HotelsSearch = forwardRef(
  (
    {
      isOnTargetPath,
      background,
      text,
      onCloseMobileSearch,
      onSearchStart,
      onSearchComplete,
      isMobile,
      isLoading = false,
    },
    ref
  ) => {
    const { t } = useTranslation('layout');
    const history = useHistory();
    const { pathname, search } = useLocation();

    const { registerOnSearch } = useGlobal();

    const [hotelDestinations, setHotelDestinations] = useState(dataDefaultValues);
    const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());
    const [isDestinationEmpty, setIsDestinationEmpty] = useState(false);
    const [isDatesEmpty, setIsDatesEmpty] = useState(false);
    const [isAdultsEmpty, setIsAdultsEmpty] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [openToast, setOpenToast] = useState(false);

    const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);

    const initialHotelFormInput = useMemo(
      () =>
        initialHotelSetter(
          !!search && pathname === BUSCAR_X_HOTELES
            ? new URLSearchParams(search)
            : undefined
        ),
      [search, pathname]
    );

    const [hotelFormInput, setHotelFormInput] = useState(
      initialHotelFormInput || {
        destination: null,
        checkInDate: null,
        checkOutDate: null,
        rooms: 1,
        adultsPerRoom: [2],
        childrenPerRoom: [0],
        childrenAges: [[]],
        residenceCountry: '',
      }
    );

    const onMobileSearchComplete = useCallback(() => {
      if (onSearchComplete) {
        onSearchComplete();
      }
      setIsMobileSearchActive(false);
    }, [onSearchComplete]);

    const handleMobileSearchComplete = useCallback(() => {
      onMobileSearchComplete();
    }, [onMobileSearchComplete]);

    const isFormValid = useMemo(() => {
      const totalAdults = hotelFormInput?.adultsPerRoom?.reduce((sum, adults) => sum + adults, 0) || 0;
      return (
        hotelFormInput?.destination &&
        hotelFormInput?.checkInDate &&
        hotelFormInput?.checkOutDate &&
        totalAdults >= 1
      );
    }, [
      hotelFormInput?.destination,
      hotelFormInput?.checkInDate,
      hotelFormInput?.checkOutDate,
      hotelFormInput?.adultsPerRoom,
    ]);

    useImperativeHandle(ref, () => ({
      openFirstDialog: () => { },
      getFormInput: () => hotelFormInput,
      triggerSearch: () => {
        handleSearchButton({ preventDefault: () => { } });
      },
      isFormValid: () => {
        const totalAdults = hotelFormInput?.adultsPerRoom?.reduce((sum, adults) => sum + adults, 0) || 0;
        return (
          hotelFormInput?.destination &&
          hotelFormInput?.checkInDate &&
          hotelFormInput?.checkOutDate &&
          totalAdults >= 1
        );
      },
    }));

    const showError = useCallback((message) => {
      setErrorMessage(message);
      setOpenToast(true);
    }, []);

    const handleCloseToast = useCallback(() => {
      setOpenToast(false);
    }, []);

    const fetchDestinations = useCallback(async (searchTerm) => {
      if (!searchTerm || 
          typeof searchTerm !== 'string' || 
          searchTerm.trim().length === 0 || 
          searchTerm.includes('undefined')) {
        setHotelDestinations({ isLoading: false, error: false, data: [] });
        return;
      }

      try {
        setHotelDestinations({ isLoading: true, error: false, data: [] });
        const response = await PackagesApi.getAirports({ cityName: searchTerm });

        const cityMap = new Map();

        (response || []).forEach((dest) => {
          const cityKey = `${dest.cityCode}-${dest.countryCode}`;

          if (!cityMap.has(cityKey)) {
            cityMap.set(cityKey, dest);
          } else {
            const existing = cityMap.get(cityKey);
            if (dest.cityCode === dest.airportCode && existing.cityCode !== existing.airportCode) {
              cityMap.set(cityKey, dest);
            }
          }
        });

        const uniqueDestinations = Array.from(cityMap.values());

        setHotelDestinations({ isLoading: false, error: false, data: uniqueDestinations });
      } catch (error) {
        showError(error?.message);
        setHotelDestinations({ data: [], error: true, isLoading: false });
      }
    }, [showError]);

    const debouncedFetchDestinations = useCallback(
      debounce((searchTerm) => {
        fetchDestinations(searchTerm);
      }, 300),
      [fetchDestinations]
    );

    const handleHotelDestination = useCallback(
      (destination) => {
        setHotelFormInput((prevState) => {
          const newDestination = destination
            ? {
              id: destination.id,
              cityCode: destination.cityCode || destination.id,
              cityName: destination.cityName || destination.name,
            }
            : null;

          const prevDest = prevState.destination;
          if (
            prevDest?.id === newDestination?.id &&
            prevDest?.cityCode === newDestination?.cityCode &&
            prevDest?.cityName === newDestination?.cityName
          ) {
            return prevState;
          }

          return {
            ...prevState,
            destination: newDestination,
          };
        });
        setIsDestinationEmpty(false);
      },
      []
    );

    const handleDestinationSearch = useCallback(
      (searchValue) => {
        if (searchValue && searchValue.trim().length > 0) {
          debouncedFetchDestinations(searchValue);
        } else {
          setHotelDestinations({ isLoading: false, error: false, data: [] });
        }
      },
      [debouncedFetchDestinations]
    );

    const handleDateRangeSelect = useCallback((startDate, endDate) => {
      setHotelFormInput((prevState) => ({
        ...prevState,
        checkInDate: startDate,
        checkOutDate: endDate,
      }));
      setIsDatesEmpty(false);
    }, []);

    const handleOccupationChange = useCallback((occupancy) => {
      setHotelFormInput((prevState) => ({
        ...prevState,
        rooms: occupancy.rooms,
        adultsPerRoom: occupancy.adultsPerRoom,
        childrenPerRoom: occupancy.childrenPerRoom,
        childrenAges: occupancy.childrenAges,
      }));
      setIsAdultsEmpty(false);
    }, []);

    const handleResidenceCountryChange = useCallback((countryCode) => {
      setHotelFormInput((prevState) => {
        if (prevState.residenceCountry === countryCode) {
          return prevState;
        }

        return {
          ...prevState,
          residenceCountry: countryCode,
        };
      });
    }, []);

    const getHotelQueryString = useCallback(() => {
      const {
        destination,
        checkInDate,
        checkOutDate,
        rooms,
        adultsPerRoom,
        childrenPerRoom,
        childrenAges,
        residenceCountry,
      } = hotelFormInput;

      const params = new URLSearchParams();

      if (destination && destination.cityCode) {
        params.set('destinationId', destination.cityCode);
        params.set('destinationName', destination.cityName);
      }

      if (checkInDate) {
        params.set(
          'checkInDate',
          formatWithMomentFormat(checkInDate, DATE_KEYS.FORMAT.ISO)
        );
      }

      if (checkOutDate) {
        params.set(
          'checkOutDate',
          formatWithMomentFormat(checkOutDate, DATE_KEYS.FORMAT.ISO)
        );
      }

      if (rooms) params.set('rooms', rooms);
      if (adultsPerRoom && adultsPerRoom.length > 0) {
        params.set('adultsPerRoom', JSON.stringify(adultsPerRoom));
      }
      if (childrenPerRoom && childrenPerRoom.length > 0) {
        params.set('childrenPerRoom', JSON.stringify(childrenPerRoom));
      }
      if (childrenAges && childrenAges.length > 0) {
        params.set('childrenAges', JSON.stringify(childrenAges));
      }
      if (residenceCountry) {
        params.set('residenceCountry', residenceCountry);
      }

      params.set('cache', getCacheStatus());
      params.set('_refresh', refreshTimestamp);

      return `?${params.toString()}`;
    }, [hotelFormInput, refreshTimestamp]);

    const linkTo = useMemo(
      () => ({
        pathname: BUSCAR_X_HOTELES,
        search: getHotelQueryString(),
      }),
      [getHotelQueryString]
    );

    const handleSearchButton = useCallback(
      (e) => {
        e.preventDefault();

        if (!hotelFormInput.destination) {
          setIsDestinationEmpty(true);
          showError('Por favor seleccione un destino');
          return;
        }

        if (!hotelFormInput.checkInDate) {
          setIsDatesEmpty(true);
          showError('Por favor seleccione la fecha de check-in');
          return;
        }

        if (!hotelFormInput.checkOutDate) {
          setIsDatesEmpty(true);
          showError('Por favor seleccione la fecha de check-out');
          return;
        }

        const totalAdults = hotelFormInput.adultsPerRoom?.reduce((sum, adults) => sum + adults, 0) || 0;
        if (totalAdults < 1) {
          setIsAdultsEmpty(true);
          showError('Por favor seleccione la cantidad de adultos');
          return;
        }

        if (
          hotelFormInput.checkOutDate &&
          hotelFormInput.checkInDate &&
          hotelFormInput.checkOutDate <= hotelFormInput.checkInDate
        ) {
          setIsDatesEmpty(true);
          showError('La fecha de check-out debe ser posterior a la fecha de check-in');
          return;
        }

        try {
          if (onSearchStart) {
            onSearchStart();
          }

          setRefreshTimestamp(Date.now());

          const searchUrl = linkTo.pathname + getHotelQueryString();
          history.push(searchUrl);

          if (registerOnSearch) {
            registerOnSearch();
          }

          if (isMobile && onMobileSearchComplete) {
            onMobileSearchComplete();
          }
        } catch (error) {
          showError(error?.message);
          if (onSearchComplete) {
            onSearchComplete();
          }
        }
      },
      [
        linkTo,
        hotelFormInput,
        getHotelQueryString,
        history,
        registerOnSearch,
        showError,
        isMobile,
        onMobileSearchComplete,
        onSearchStart,
        onSearchComplete,
      ]
    );

    const isSearchButtonDisabled = () => {
      const hasDestination = !!hotelFormInput.destination;
      const hasDates = !!(hotelFormInput.checkInDate && hotelFormInput.checkOutDate);
      const hasValidDates =
        hasDates && hotelFormInput.checkOutDate > hotelFormInput.checkInDate;
      const totalAdults = hotelFormInput.adultsPerRoom?.reduce((sum, adults) => sum + adults, 0) || 0;
      const hasAdults = totalAdults >= 1;

      return !(hasDestination && hasValidDates && hasAdults);
    };

    if (isMobile) {
      return (
        <>
          <div className='package-search__form__container'>
            <div className='package-search__form__fields'>
              <HotelFilterForm
                destinations={hotelDestinations}
                handleDestinationSelect={handleHotelDestination}
                handleDestinationSearch={handleDestinationSearch}
                handleDateRangeSelect={handleDateRangeSelect}
                handleOccupancyChange={handleOccupationChange}
                handleResidenceCountryChange={handleResidenceCountryChange}
                formInput={hotelFormInput}
                isDestinationEmpty={isDestinationEmpty}
                setIsDestinationEmpty={setIsDestinationEmpty}
                isDatesEmpty={isDatesEmpty}
                setIsDatesEmpty={setIsDatesEmpty}
                onMobileSearchComplete={handleMobileSearchComplete}
                isMobileSearchActive={isMobileSearchActive}
              />
            </div>
          </div>

          <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
        </>
      );
    }

    if (!isOnTargetPath) return <></>;

    return (
      <>
        <div className='package-search__form__container'>
          <div
            className='package-search__form__fields'
            style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
          >
            <HotelFilterForm
              destinations={hotelDestinations}
              handleDestinationSelect={handleHotelDestination}
              handleDestinationSearch={handleDestinationSearch}
              handleDateRangeSelect={handleDateRangeSelect}
              handleOccupancyChange={handleOccupationChange}
              handleResidenceCountryChange={handleResidenceCountryChange}
              formInput={hotelFormInput}
              isDestinationEmpty={isDestinationEmpty}
              setIsDestinationEmpty={setIsDestinationEmpty}
              isDatesEmpty={isDatesEmpty}
              setIsDatesEmpty={setIsDatesEmpty}
              onMobileSearchComplete={handleMobileSearchComplete}
              isMobileSearchActive={isMobileSearchActive}
            />
            <div className='package-search__button-row'>
              <button
                onClick={handleSearchButton}
                disabled={isSearchButtonDisabled()}
                className='package-search__button big-package-search__button'
                style={{
                  backgroundColor: background,
                  color: text,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                }}
              >
                <i className='fa fa-search heading__icon' />
                {t('common.search') || 'Buscar'}
              </button>
            </div>
          </div>
        </div>

        <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
      </>
    );
  }
);

export default HotelsSearch;
